import { useState, useRef, useEffect, FC, useMemo } from 'react';
import { CountryProps, DropDownInputProps, Option } from './dropDownInput.type';
import DownArrow from '../../assets/down-arrow.png';
import FlagIcon from '../flagIcon/flagIcon';
import { addElipsesToText, getIdAttributes } from '../../utils/helper';
import { useTranslation, Trans } from 'react-i18next';

export const idPrefix: string = 'dropdown-input';

// --- Helper Functions ---

/**
 * Returns the selected option labels as an array for proper translation.
 */
const getSelectedLabels = <T extends string | number>(
  props: Pick<DropDownInputProps<T>, 'isMulti' | 'selected' | 'options'>,
): string[] => {
  const { isMulti, selected, options = [] } = props;

  if (isMulti && Array.isArray(selected) && selected.length > 0) {
    return options
      .filter((opt) => selected.includes(opt.value))
      .map((opt) => opt.label)
      .filter(Boolean);
  }

  const selectedOption = options.find((opt) => opt.value === selected);
  return selectedOption ? [selectedOption.label] : [];
};

// --- Sub-Components ---

const DropdownButton: FC<
  Pick<
    DropDownInputProps,
    'disabled' | 'errorMessage' | 'buttonClassName' | 'placeholder'
  > & {
    onClick: () => void;
    selectedLabels: string[];
  }
> = ({
  disabled,
  onClick,
  errorMessage,
  buttonClassName,
  placeholder,
  selectedLabels,
}) => {
  const { t } = useTranslation();

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={[
        'w-full flex items-center justify-between h-[40px] px-4 py-2 rounded-full bg-[#FAFAFA] text-sm text-gray-700',
        errorMessage ? 'border border-[#B60000]' : '',
        disabled ? 'opacity-50 cursor-not-allowed' : '',
        buttonClassName,
      ]
        .filter(Boolean)
        .join(' ')}
      {...getIdAttributes(idPrefix, 'button')}
    >
      <span
        className={`${
          selectedLabels.length > 0 ? 'text-black' : 'text-gray-400'
        } bg-[#FAFAFA] min-h-[20px]`}
        {...getIdAttributes(idPrefix, 'selected-label')}
      >
        {selectedLabels.length > 0
          ? addElipsesToText(
              selectedLabels.map((label) => t(label)).join(', '),
              60,
            )
          : placeholder}
      </span>
      <img
        src={DownArrow}
        alt="Toggle Dropdown"
        className="w-[11px] h-[6px]"
        {...getIdAttributes(idPrefix, 'arrow')}
      />
    </button>
  );
};

const CountryButton: FC<
  Pick<
    DropDownInputProps,
    'disabled' | 'selectedCountry' | 'buttonClassName'
  > & {
    onClick: () => void;
  }
> = ({ disabled, selectedCountry, buttonClassName, onClick }) => {
  if (!selectedCountry) return null;

  return (
    <div className="flex items-center gap-2 w-full">
      <button
        onClick={onClick}
        disabled={disabled}
        className={[
          'w-full flex justify-between items-center px-4 py-2 rounded-full bg-[#FAFAFA] text-sm text-gray-700',
          disabled ? 'opacity-50 cursor-not-allowed' : '',
          buttonClassName,
        ]
          .filter(Boolean)
          .join(' ')}
        {...getIdAttributes(idPrefix, 'country-btn')}
      >
        <div
          className="flex"
          {...getIdAttributes(idPrefix, 'country-flag-row')}
        >
          <FlagIcon
            svg={selectedCountry.flag}
            className="w-5 h-[12px] rounded-none mt-1"
          />
          <span className="px-2">{selectedCountry.dialCode}</span>
        </div>
        <img
          src={DownArrow}
          alt="Toggle Dropdown"
          className="w-[11px] h-[6px]"
          {...getIdAttributes(idPrefix, 'country-arrow')}
        />
      </button>
    </div>
  );
};

const DropdownPanel = <T extends string | number>({
  props,
  setIsOpen,
  handleSelect,
  handleCountrySelect,
  isSelected,
}: {
  props: DropDownInputProps<T>;
  setIsOpen: (isOpen: boolean) => void;
  handleSelect: (value: T) => void;
  handleCountrySelect: (country: CountryProps) => void;
  isSelected: (value: T) => boolean;
}) => {
  const {
    isCountry,
    className,
    selectedCountry,
    countries,
    isMulti,
    selected,
    options = [],
    placeholder,
    search,
    onSelect,
  } = props;

  const [searchTerm, setSearchTerm] = useState('');

  const filteredOptions = useMemo(
    () =>
      options.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    [options, searchTerm],
  );

  const handleSelectAll = () => {
    if (!onSelect) return;

    const currentSelection = Array.isArray(selected) ? selected : [];
    const filteredValues = filteredOptions.map((opt) => opt.value);

    const allFilteredSelected =
      filteredValues.length > 0 &&
      filteredValues.every((val) => currentSelection.includes(val));

    let newSelection: T[];
    if (allFilteredSelected) {
      const filteredValuesSet = new Set(filteredValues);
      newSelection = currentSelection.filter(
        (val) => !filteredValuesSet.has(val),
      );
    } else {
      const newItems = filteredValues.filter(
        (val) => !currentSelection.includes(val),
      );
      newSelection = [...currentSelection, ...newItems];
    }
    onSelect(newSelection);
  };

  const areAllFilteredSelected =
    isMulti &&
    Array.isArray(selected) &&
    filteredOptions.length > 0 &&
    filteredOptions.every((opt) => selected.includes(opt.value));

  return (
    <div
      className={[
        'z-50 bg-white rounded-[20px] shadow-lg border border-gray-200 overflow-hidden',
        isCountry ? 'inset-x-0 mt-2 absolute' : 'mt-0 absolute top-[100%]',
        className ?? 'w-[360px]',
      ]
        .filter(Boolean)
        .join(' ')}
      {...getIdAttributes(idPrefix, 'dropdown')}
    >
      {isCountry ? (
        <>
          <div
            className="flex justify-between border-b text-sm px-4 py-2"
            {...getIdAttributes(idPrefix, 'country-header')}
          >
            {selectedCountry && (
              <div className="flex">
                <FlagIcon
                  svg={selectedCountry.flag}
                  className="w-5 h-[12px] rounded-none mt-1"
                />
                <span className="ml-2">{selectedCountry.dialCode}</span>
                <span className="px-4">{selectedCountry.name}</span>
              </div>
            )}
            <img
              src={DownArrow}
              alt="Show Dropdown"
              className="w-[11px] h-[7px] mt-2"
              {...getIdAttributes(idPrefix, 'country-header-arrow')}
            />
          </div>
          <ul
            className="max-h-60 overflow-y-auto"
            {...getIdAttributes(idPrefix, 'country-list')}
          >
            {(countries ?? []).map((country: CountryProps) => (
              <li
                key={country.code}
                onClick={() => handleCountrySelect(country)}
                className="flex items-center gap-3 px-4 py-2 cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                {...getIdAttributes(idPrefix, `country-option-${country.code}`)}
              >
                <FlagIcon
                  svg={country.flag}
                  className="w-5 h-[12px] rounded-none object-contain"
                />
                <span className="text-sm">{country.dialCode}</span>
                <span className="text-sm">{country.name}</span>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <>
          <div
            className="flex justify-between border-b text-sm px-4 py-2 sticky top-0 bg-white z-10 max-h-32 overflow-y-auto"
            {...getIdAttributes(idPrefix, 'dropdown-header')}
          >
            <span>
              {getSelectedLabels({ isMulti, selected, options }).length > 0
                ? getSelectedLabels({ isMulti, selected, options })
                    .map((label) => <Trans key={label} i18nKey={label} />)
                    .reduce((prev, curr) => (
                      <>
                        {prev}
                        {', '}
                        {curr}
                      </>
                    ))
                : placeholder}
            </span>
            <img
              src={DownArrow}
              alt="Close Dropdown"
              className="w-[11px] h-[7px] mt-2 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                setIsOpen(false);
              }}
              {...getIdAttributes(idPrefix, 'dropdown-header-arrow')}
            />
          </div>

          {(search || isMulti) && (
            <div className="p-2 border-b">
              {search && (
                <input
                  type="text"
                  placeholder="Search..."
                  className="w-full px-3 py-1.5 text-sm border rounded-full"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
              {isMulti && (
                <div
                  className="flex items-center gap-2 mt-2 px-2 py-1 cursor-pointer"
                  onClick={handleSelectAll}
                >
                  <input
                    type="checkbox"
                    checked={areAllFilteredSelected}
                    readOnly
                    className="form-checkbox rounded border-gray-300 accent-THEME_COLOR"
                  />
                  <span className="text-sm font-medium">Select All</span>
                </div>
              )}
            </div>
          )}

          <div
            className="max-h-72 overflow-y-auto"
            {...getIdAttributes(idPrefix, 'dropdown-list')}
          >
            {filteredOptions.map((option: Option<T>) => {
              const selectedItem = isSelected(option.value);
              return (
                <div
                  {...getIdAttributes(idPrefix, `option-${option.value}`)}
                  key={`${option.value}`}
                  onClick={() => handleSelect(option.value)}
                  className={`flex items-center gap-2 px-4 py-2 text-sm cursor-pointer hover:bg-gray-100 ${
                    selectedItem ? 'bg-gray-100 font-medium' : ''
                  }`}
                >
                  {isMulti && (
                    <input
                      type="checkbox"
                      checked={selectedItem}
                      readOnly
                      className="form-checkbox rounded border-gray-300 accent-THEME_COLOR"
                    />
                  )}
                  <span>
                    <Trans i18nKey={option.label} />
                  </span>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

// --- Main Component ---

const DropDownInput = <T extends string | number>(
  props: DropDownInputProps<T>,
) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    isMulti,
    selected,
    disabled,
    onSelect,
    isCountry,
    onSelectCountry,
    errorMessage,
  } = props;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (value: T) => {
    if (disabled || !onSelect) return;

    if (isMulti) {
      const currentSelection = Array.isArray(selected) ? selected : [];
      const newSelection = currentSelection.includes(value)
        ? currentSelection.filter((v) => v !== value)
        : [...currentSelection, value];
      onSelect(newSelection as T[]);
    } else {
      onSelect(value);
      setIsOpen(false);
    }
  };

  const handleCountrySelect = (country: CountryProps) => {
    if (disabled) return;
    onSelectCountry?.(country);
    setIsOpen(false);
  };

  const isSelected = (value: T): boolean =>
    isMulti
      ? Array.isArray(selected) && selected.includes(value)
      : selected === value;

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen((prev) => !prev);
    }
  };

  return (
    <div
      className="relative w-full"
      ref={dropdownRef}
      {...getIdAttributes(idPrefix, 'container')}
    >
      {isCountry ? (
        <CountryButton {...props} onClick={toggleDropdown} />
      ) : (
        !isOpen && (
          <DropdownButton
            {...props}
            onClick={toggleDropdown}
            selectedLabels={getSelectedLabels(props)}
          />
        )
      )}

      {!isOpen && errorMessage && (
        <div
          className="text-[#B60000] text-sm mt-1"
          {...getIdAttributes(idPrefix, 'error')}
        >
          {errorMessage}
        </div>
      )}

      {isOpen && !disabled && (
        <DropdownPanel
          props={props}
          setIsOpen={setIsOpen}
          handleSelect={handleSelect}
          handleCountrySelect={handleCountrySelect}
          isSelected={isSelected}
        />
      )}
    </div>
  );
};

export default DropDownInput;
